import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  EyeInvisible,
  EyeVisible,
  LayersFront,
  Pencil,
  Plus,
} from '@factorialco/f0-react/icons/app'
import {
  FormsCustomTemplatesDocument,
  FormsCustomTemplatesFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import {
  useDraftTemplate,
  useDuplicateTemplate,
  usePublishTemplate,
} from 'modules/forms/hooks/useFormTemplates'

import DeleteFormTemplate from './actions/DeleteFormTemplate'
import PublishToUseDialog from './actions/PublishToUseDialog'
import { createDraftAction } from './actions/draft'
import { createDuplicateAction } from './actions/duplicate'
import { createPublishAction } from './actions/publish'
import {
  type ColumnConfigurations,
  type TableColumnType,
  DEFAULT_CARD_COLUMNS,
  DEFAULT_TABLE_COLUMNS,
  getDefaultColumnConfigurations,
} from './properties/definitions'
import { createCardProperties } from './properties/card'
import { createListProperties } from './properties/list'
import { createTableProperties } from './properties/table'
import { buildVisualizations } from './visualizations'

type Props = {
  resourceType: string
  columns?: TableColumnType[]
  configurations?: ColumnConfigurations
  routes?: {
    create: string
    edit?: (itemId: string) => string
    use?: (itemId: string) => string
  }
  onlyRead?: boolean
  onlyActive?: boolean
  ignoreStatus?: boolean
  mode?: 'table' | 'list' | 'card' | 'table+card' | 'all'
} & (
  | {
      itemOnClick: (item: FormsCustomTemplatesFragment) => void
    }
  | {
      itemUrl: (item: FormsCustomTemplatesFragment) => string
    }
)

const FormsTemplatesDataCollection = ({
  resourceType,
  columns: columnsProp,
  configurations: configurationsProp,
  routes,
  onlyRead,
  onlyActive,
  ignoreStatus = false,
  mode = 'table+card',
  ...props
}: Props) => {
  const navigate = useNavigate()
  const [templateIdToDelete, setTemplateIdToDelete] = useState<string | undefined>(undefined)
  const [draftTemplateToUse, setDraftTemplateToUse] = useState<string | undefined>(undefined)

  const filterStatus = (cols: TableColumnType[]) =>
    ignoreStatus ? cols.filter((c) => c !== 'status') : cols

  const tableColumnTypes = filterStatus(columnsProp ?? DEFAULT_TABLE_COLUMNS)
  const cardColumnTypes = filterStatus(columnsProp ?? DEFAULT_CARD_COLUMNS)

  const configurations = useMemo(
    () => ({ ...getDefaultColumnConfigurations(), ...configurationsProp }),
    [configurationsProp]
  )

  const tableColumns = useMemo(
    () => createTableProperties(tableColumnTypes, configurations),
    [tableColumnTypes, configurations]
  )

  const listFields = useMemo(
    () => createListProperties(tableColumnTypes, configurations),
    [tableColumnTypes, configurations]
  )

  const cardProperties = useMemo(
    () => createCardProperties(cardColumnTypes, configurations),
    [cardColumnTypes, configurations]
  )

  const hasCardView = mode === 'card' || mode === 'table+card'

  const { publishFormTemplate } = usePublishTemplate()
  const { draftFormTemplate } = useDraftTemplate()
  const { duplicateFormTemplate } = useDuplicateTemplate()

  const onPublishTemplate = createPublishAction(publishFormTemplate, resourceType)
  const onDraftTemplate = createDraftAction(draftFormTemplate, resourceType)
  const onDuplicateTemplate = createDuplicateAction(duplicateFormTemplate)

  const source = useGraphqlDataCollectionSource({
    document: FormsCustomTemplatesDocument,
    mapData: (data) => data.forms.formsConnection.nodes,
    search: {
      enabled: true,
      sync: true,
    },
    mapVariables: ({ search }) => ({
      resourceType,
      search,
      onlyTemplates: true,
      onlyActive,
    }),
    mapPagination: (data) => ({
      total: data.forms.formsConnection.totalCount ?? 0,
    }),
    itemOnClick:
      'itemOnClick' in props
        ? (template: FormsCustomTemplatesFragment) => () => props.itemOnClick(template)
        : undefined,
    itemUrl:
      'itemUrl' in props
        ? (template: FormsCustomTemplatesFragment) => props.itemUrl(template)
        : undefined,
    primaryActions: onlyRead
      ? undefined
      : () => ({
          label: i18n.t('forms.custom_templates.data_collection.new_template'),
          icon: Add,
          onClick: () => {
            if (routes?.create) {
              navigate(routes.create)
            }
          },
        }),
    itemActions: (template: FormsCustomTemplatesFragment) =>
      onlyRead
        ? []
        : [
            !ignoreStatus
              ? template.status == 'draft'
                ? {
                    label: i18n.t('forms.custom_templates.data_collection.actions.publish'),
                    icon: EyeVisible,
                    onClick: () => onPublishTemplate(template.id),
                  }
                : {
                    label: i18n.t('forms.custom_templates.data_collection.actions.draft'),
                    icon: EyeInvisible,
                    onClick: () => onDraftTemplate(template.id),
                  }
              : undefined,
            {
              label: i18n.t('forms.custom_templates.data_collection.actions.duplicate'),
              icon: LayersFront,
              onClick: () =>
                onDuplicateTemplate(
                  template.id,
                  template.resourceType,
                  template.title,
                  template.description ?? undefined
                ),
            },
            {
              label: i18n.t('forms.custom_templates.data_collection.actions.delete'),
              icon: Delete,
              critical: true,
              onClick: () => setTemplateIdToDelete(template.id),
            },
            hasCardView
              ? {
                  label: i18n.t('forms.custom_templates.data_collection.actions.create_survey'),
                  type: 'secondary' as const,
                  onClick: () => {
                    if (!ignoreStatus && template.status === 'draft') {
                      setDraftTemplateToUse(template.id)
                    } else if (routes?.use) {
                      navigate(routes.use(template.id))
                    }
                  },
                }
              : undefined,
            hasCardView && routes?.edit
              ? {
                  label: i18n.t('forms.custom_templates.data_collection.actions.edit'),
                  icon: Pencil,
                  type: 'secondary' as const,
                  onClick: () => {
                    if (routes?.edit) {
                      navigate(routes.edit(template.id))
                    }
                  },
                }
              : undefined,
          ].filter(Boolean),
  })

  const visualizations = useMemo(
    () => buildVisualizations(mode, { tableColumns, listFields, cardProperties }),
    [mode, tableColumns, listFields, cardProperties]
  )

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={visualizations}
        emptyStates={{
          'no-data': {
            title: i18n.t('forms.custom_templates.empty_state.title'),
            description: i18n.t('forms.custom_templates.empty_state.description'),
            actions: [
              {
                label: i18n.t('forms.custom_templates.data_collection.new_template'),
                icon: Plus,
                onClick: () => {
                  if (routes?.create) {
                    navigate(routes.create)
                  }
                },
              },
            ],
          },
        }}
      />
      {templateIdToDelete && (
        <DeleteFormTemplate
          templateId={templateIdToDelete}
          onClose={() => setTemplateIdToDelete(undefined)}
        />
      )}
      {draftTemplateToUse && (
        <PublishToUseDialog
          onConfirm={async () => {
            const result = await onPublishTemplate(draftTemplateToUse)
            if (result.success && routes?.use) {
              navigate(routes.use(draftTemplateToUse))
            }
            setDraftTemplateToUse(undefined)
          }}
          onClose={() => setDraftTemplateToUse(undefined)}
        />
      )}
    </>
  )
}

export default FormsTemplatesDataCollection
